import { RegisterScreen } from '@/components/RegisterScreen';
import { StatusPill } from '@/components/live';
import { optionLabel } from '@/lib/practiceLists';

interface DocumentTypeRow {
  id: string; document_type_code: string; name: string; module_key: string; sensitive_class: string;
  validity_required: boolean; allowed_mime_types: string[]; status: string; is_system: boolean; documents_count: number;
}

/** The screens a document can be filed under - `module_key` is a screen permission key. */
const MODULES = [
  { id: 'farmer.register', label: 'Farmer register' },
  { id: 'party.kyc', label: 'Party KYC' },
  { id: 'party.consent', label: 'Consent' },
  { id: 'party.register', label: 'Parties & stakeholders' },
  { id: 'plot.register', label: 'Plots' },
  { id: 'plot.tenure', label: 'Plot tenure' },
  { id: 'plot.mapping', label: 'Plot mapping & evidence' },
  { id: 'plot.inventory', label: 'Tree inventory & practices' },
  { id: 'plot.harvest', label: 'Harvest & trade' },
  { id: 'plot.monitoring', label: 'Satellite monitoring' },
  { id: 'site.register', label: 'Work sites' },
  { id: 'evidence.pack', label: 'Evidence packs' },
  { id: 'document.library', label: 'Generated documents' },
  { id: 'org.profile', label: 'Organisation' },
];

const SENSITIVITY = [
  { id: 'public', label: 'Public' },
  { id: 'pii', label: 'Personal (PII)' },
  { id: 'bank', label: 'Bank' },
  { id: 'legal', label: 'Legal' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'audit', label: 'Audit evidence' },
  { id: 'hcv', label: 'High conservation value' },
];

const FORMATS = [
  { id: 'application/pdf', label: 'PDF' },
  { id: 'image/jpeg', label: 'JPEG image' },
  { id: 'image/png', label: 'PNG image' },
  { id: 'image/webp', label: 'WebP image' },
  { id: 'image/heic', label: 'HEIC image (iPhone)' },
  { id: 'text/html', label: 'HTML (generated)' },
  { id: 'application/msword', label: 'Word (.doc)' },
  { id: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', label: 'Word (.docx)' },
  { id: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', label: 'Excel (.xlsx)' },
  { id: 'text/csv', label: 'CSV' },
];

const STATUS = [
  { id: 'active', label: 'Active' },
  { id: 'draft', label: 'Draft' },
  { id: 'inactive', label: 'Inactive' },
];

const formatLabels = (mimes: string[]) => mimes.map((mime) => optionLabel(FORMATS, mime)).join(', ');

/**
 * Document types: every kind of file the organisation stores, the formats it accepts and how
 * sensitive it is. The eight codes the application reads by name (KYC_SCAN, FARMER_PHOTO ...) keep
 * their code and stay active - `doc.fn_document_type_guard` refuses otherwise - and a type that
 * files already carry cannot be removed, only switched to inactive.
 */
export default function DocumentTypesScreen() {
  return (
    <RegisterScreen<DocumentTypeRow>
      secondary={{ sum: (row) => row.documents_count, unit: 'files', digits: 0 }}
      addLabel="Add document type" exportName="document-types" recordLabel={(row) => `document type ${row.document_type_code}`}
      recordNoun="document type"
      screenKey="document.type.master" searchColumns={['document_type_code', 'name', 'module_key']}
      subtitle="Every kind of file the organisation stores - the formats it accepts, how sensitive it is and whether it expires."
      table="doc.document_types" title="Document types" view="document_types" orderBy={{ column: 'name', ascending: true }}
      columns={[
        { key: 'name', label: 'Document type', render: (row) => <span className="cell-stack"><strong>{row.name}</strong><small className="muted">{row.document_type_code}{row.is_system ? ' · used by the application' : ''}</small></span> },
        { key: 'module_key', label: 'Filed under', render: (row) => optionLabel(MODULES, row.module_key) },
        { key: 'sensitive_class', label: 'Sensitivity', render: (row) => optionLabel(SENSITIVITY, row.sensitive_class) },
        { key: 'allowed_mime_types', label: 'Formats', render: (row) => <span className="muted">{formatLabels(row.allowed_mime_types ?? [])}</span> },
        { key: 'validity_required', label: 'Expires', render: (row) => (row.validity_required ? 'Yes' : 'No') },
        { key: 'documents_count', label: 'Files', align: 'right', render: (row) => row.documents_count },
        { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
      ]}
      stats={[
        { key: 'active', label: 'Active', tone: 'ok', match: (row) => row.status === 'active' },
        { key: 'system', label: 'Used by the application', tone: 'info', match: (row) => row.is_system },
        { key: 'sensitive', label: 'Personal or bank', tone: 'warn', match: (row) => ['pii', 'bank'].includes(row.sensitive_class) },
        { key: 'expiring', label: 'Expire', tone: 'neutral', match: (row) => row.validity_required },
        { key: 'inactive', label: 'Inactive', tone: 'neutral', match: (row) => row.status !== 'active' },
      ]}
      defaults={{ sensitive_class: 'public', status: 'active', validity_required: false, allowed_mime_types: ['application/pdf', 'image/jpeg', 'image/png'] }}
      fields={[
        { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'e.g. Transit permit' },
        { key: 'document_type_code', label: 'Code', type: 'text', required: true, placeholder: 'e.g. TRANSIT_PERMIT',
          hint: 'Capital letters, digits and underscores. Fixed once files carry it, and always for the types the application uses.' },
        { key: 'module_key', label: 'Filed under', type: 'select', options: MODULES, required: true },
        { key: 'sensitive_class', label: 'Sensitivity', type: 'select', options: SENSITIVITY, required: true,
          hint: 'Personal and bank documents are masked for roles without the reveal right.' },
        { key: 'allowed_mime_types', label: 'Accepted formats', type: 'multi', options: FORMATS, required: true },
        { key: 'validity_required', label: 'Validity', type: 'check', placeholder: 'This document expires - ask for a valid-to date' },
        { key: 'status', label: 'Status', type: 'select', options: STATUS, required: true,
          hint: 'Inactive stops new files of this type; files already stored keep it. Types the application uses stay active.' },
      ]}
      validate={(form) => ({
        document_type_code: /^[A-Za-z][A-Za-z0-9_]{1,39}$/.test(String(form.document_type_code ?? '').trim())
          ? undefined : '2 to 40 letters, digits or underscores, starting with a letter.',
        allowed_mime_types: Array.isArray(form.allowed_mime_types) && form.allowed_mime_types.length === 0
          ? 'Choose at least one format.' : undefined,
      })}
    />
  );
}
